/**
 * "Get notified" modal.
 *
 * Everything this element does is an upgrade on markup that already works: the
 * <dialog> closes by itself because both dismiss controls are
 * <form method="dialog"> submits, and showModal() gives the focus trap, the
 * backdrop and Escape-to-close for free. This script only decides *when* to
 * open, and remembers that a shopper has already answered.
 *
 * It opens nothing unless a merchant picks a trigger — the Figma frames draw
 * the modal open and specify no trigger, so "off" is the default.
 */

if (!customElements.get('recipe-notify-modal')) {
  customElements.define(
    'recipe-notify-modal',
    class RecipeNotifyModal extends HTMLElement {
      constructor() {
        super();
        this.onScroll = this.onScroll.bind(this);
        this.onClose = this.onClose.bind(this);
      }

      connectedCallback() {
        this.dialog = this.querySelector('dialog');
        if (!this.dialog) {
          return;
        }

        this.dialog.addEventListener('close', this.onClose);

        // Returning from the customer-form POST: re-open so the shopper sees
        // the result of the submit they just made, whatever the trigger says,
        // and whether or not they have dismissed the modal before. The marker is
        // rendered inside {% form 'customer' %} because Liquid's `form` drop does
        // not exist on this element — it renders before the form tag opens.
        if (this.querySelector('[data-notify-result]')) {
          this.open();
          return;
        }

        if (this.alreadyAnswered()) {
          return;
        }

        const trigger = this.dataset.trigger;
        if (trigger === 'delay') {
          const seconds = Number(this.dataset.delay) || 15;
          this.timer = window.setTimeout(() => this.open(), seconds * 1000);
        } else if (trigger === 'scroll') {
          window.addEventListener('scroll', this.onScroll, { passive: true });
        }
      }

      disconnectedCallback() {
        window.clearTimeout(this.timer);
        window.removeEventListener('scroll', this.onScroll);
        this.dialog?.removeEventListener('close', this.onClose);
      }

      /**
       * @returns {Storage|null} The store this element's frequency maps onto,
       *   or null when the shopper should see the modal every visit.
       */
      store() {
        try {
          if (this.dataset.frequency === 'session') {
            return window.sessionStorage;
          }
          if (this.dataset.frequency === 'once') {
            return window.localStorage;
          }
        } catch {
          // Storage throws outright in some privacy modes rather than
          // returning null, so a shopper there simply sees it every visit.
          return null;
        }
        return null;
      }

      alreadyAnswered() {
        try {
          return this.store()?.getItem(this.dataset.storageKey) === '1';
        } catch {
          return false;
        }
      }

      markAnswered() {
        try {
          this.store()?.setItem(this.dataset.storageKey, '1');
        } catch {
          // Nothing to do — the shopper sees it again next visit.
        }
      }

      onScroll() {
        const doc = document.documentElement;
        const scrollable = doc.scrollHeight - window.innerHeight;
        if (scrollable <= 0) {
          return;
        }

        const percent = (window.scrollY / scrollable) * 100;
        if (percent >= (Number(this.dataset.scrollPercent) || 50)) {
          window.removeEventListener('scroll', this.onScroll);
          this.open();
        }
      }

      open() {
        if (this.dialog.open) {
          return;
        }
        this.dialog.showModal();
      }

      /**
       * Dismissing counts as answering, however it happened — the close button,
       * "No thanks", Escape, or a successful signup. Re-prompting someone who
       * has already said no is the behaviour this guards against.
       */
      onClose() {
        this.markAnswered();
      }
    },
  );
}
